import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { routes as rotteGenerate } from 'vue-router/auto-routes';
import type { Privilegio } from '@evolfin/shared';
import AppShell from '@/components/app/AppShell.vue';
import NonTrovataView from '@/components/app/NonTrovataView.vue';
import { EvolfinApiError, http } from '@/api/client';
import { useAuthStore } from '@/stores/auth';

declare module 'vue-router' {
  interface RouteMeta {
    /** Rotta accessibile senza sessione: resta fuori dalla shell e dalla guardia. */
    pubblica?: boolean;
    /** Privilegi necessari a VEDERE la voce: l'autorizzazione vera è server-side. */
    privilegi?: Privilegio[];
    titolo?: string;
  }
}

/**
 * Le rotte arrivano dai file (`src/features/<mondo>/pages/…`, vedi vite.config)
 * con il `meta` dichiarato da `definePage()` dentro ogni pagina. Qui restano
 * solo le tre cose che dai file non si deducono:
 *
 *  1. il layout: tutto ciò che non è pubblico vive dentro `AppShell`;
 *  2. dove punta la root;
 *  3. il catch-all 404.
 */
/**
 * Le rotte generate sono ANNIDATE: `src/features/auth/pages/login.vue` produce
 * un record `/auth` con figlio `login`, e il `meta` di `definePage()` sta sulla
 * foglia. Il `pubblica` va quindi cercato in profondità, non sul primo livello.
 */
function haFogliePubbliche(r: RouteRecordRaw): boolean {
  return Boolean(r.meta?.pubblica) || (r.children ?? []).some(haFogliePubbliche);
}
function haFoglieProtette(r: RouteRecordRaw): boolean {
  const figli = r.children ?? [];
  if (figli.length === 0) return !r.meta?.pubblica;
  return figli.some(haFoglieProtette);
}

const generate = rotteGenerate as RouteRecordRaw[];
const pubbliche = generate.filter(haFogliePubbliche);
const protette = generate.filter((r) => !haFogliePubbliche(r));

// Un sottoalbero misto finirebbe tutto da una parte sola, in silenzio: meglio
// accorgersene subito che scoprire una pagina protetta fuori dalla shell.
for (const r of pubbliche) {
  if (haFoglieProtette(r)) {
    throw new Error(
      `Rotta "${String(r.path)}": la stessa feature mescola pagine pubbliche e protette. ` +
        'Separale in due feature-folder, altrimenti il layout non può essere deciso.',
    );
  }
}

const routes: RouteRecordRaw[] = [
  ...pubbliche,
  {
    path: '/',
    component: AppShell,
    children: [
      // Il redirect della root deve stare QUI, come figlio index: un secondo
      // record di pari livello con path '/' non verrebbe mai raggiunto, perché
      // vince il layout e si finirebbe sulla shell con il contenuto vuoto.
      { path: '', redirect: '/dashboard' },
      // I path dei figli sono assoluti (`/dashboard`): vue-router li ammette e
      // così l'URL resta quello che si legge dall'albero dei file.
      ...protette,
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'non-trovata',
    meta: { pubblica: true },
    component: NonTrovataView,
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

/**
 * Guardia di navigazione: nasconde ciò che non è accessibile e riporta al
 * login quando la sessione è scaduta. NON è un controllo di sicurezza —
 * quello lo fanno i guard del server.
 */
router.beforeEach(async (to) => {
  const auth = useAuthStore();

  if (auth.principal === null) await auth.ripristina();
  if (to.meta.pubblica) return true;

  if (!auth.autenticato) return versoLogin(to.fullPath);

  const mancanti = (to.meta.privilegi ?? []).filter((p) => !auth.ha(p));
  if (mancanti.length > 0) return { path: '/dashboard' };

  return true;
});

/**
 * Il login sa perché ci si arriva: `expired` c'è solo se una sessione aperta è
 * andata persa, non per chi entra per la prima volta o è appena uscito. Il flag
 * si consuma qui, così l'avviso compare una volta sola.
 */
function versoLogin(redirect: string) {
  const auth = useAuthStore();
  const query: Record<string, string> = { redirect };
  if (auth.sessioneScaduta) {
    query.expired = '1';
    auth.sessioneScaduta = false;
  }
  return { path: '/auth/login', query };
}

// Un 401 mentre si lavora vuol dire che il server ha chiuso la sessione (scaduta
// o revocata): si torna al login invece di lasciare la pagina piena di errori.
http.interceptors.response.use(undefined, async (errore: unknown) => {
  const auth = useAuthStore();
  if (errore instanceof EvolfinApiError && errore.dettagli.statusCode === 401 && auth.autenticato) {
    auth.segnaSessioneScaduta();
    await router.replace(versoLogin(router.currentRoute.value.fullPath));
  }
  return Promise.reject(errore);
});

router.afterEach((to) => {
  document.title = to.meta.titolo ? `${to.meta.titolo} · Evolfin` : 'Evolfin';
});
